/**
 * Initials, and a stack of them.
 *
 * The colour is a hash of the user id rather than of the name, so somebody
 * changing what they are called does not change what they look like. It is
 * decoration only: every avatar carries the name next to it or in its
 * `title`, and the member stack is followed by the same names in text, so
 * nothing on either screen is identified by colour alone.
 */

import { initialsOf } from '../lib/format'

/** A hue per person, stable across renders and devices. */
function hueOf(userId: string): number {
  let hash = 0
  for (let i = 0; i < userId.length; i += 1) hash = (hash * 31 + userId.charCodeAt(i)) % 360
  return hash
}

export function Avatar({
  userId,
  displayName,
  size = 'md',
}: {
  userId: string
  displayName: string
  size?: 'sm' | 'md'
}) {
  return (
    <span
      className={`avatar avatar--${size}`}
      style={{ '--avatar-hue': hueOf(userId) } as React.CSSProperties}
      title={displayName}
    >
      <span aria-hidden="true">{initialsOf(displayName)}</span>
      <span className="visually-hidden">{displayName}</span>
    </span>
  )
}

/**
 * The group screen's member stack: overlapping avatars, then the names in
 * words. The stack is the glance; the names are the answer.
 */
export function MemberStack({
  members,
  max = 6,
}: {
  members: readonly { userId: string; displayName: string; left?: boolean }[]
  max?: number
}) {
  const shown = members.slice(0, max)
  const rest = members.length - shown.length

  return (
    <div className="stack">
      <div className="stack__avatars" aria-hidden="true">
        {shown.map((member) => (
          <Avatar key={member.userId} userId={member.userId} displayName={member.displayName} />
        ))}
        {rest > 0 && <span className="avatar avatar--md avatar--rest">+{rest}</span>}
      </div>
      <p className="stack__names">
        {members.map((member) => member.displayName).join(', ')}
      </p>
    </div>
  )
}
